/**
 * Token Expiration Modal
 *
 * Displays when user's authentication token has expired and silent refresh failed.
 * Auto-redirects to sign-in after a short countdown so that idle users are
 * returned to login promptly without having to click a button.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../Modal/Modal';

/** Seconds to wait before automatically triggering the sign-in redirect */
const AUTO_REDIRECT_SECONDS = 5;

export interface TokenExpirationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when user clicks "Sign In Again" button or auto-redirect fires */
  onSignInClick: () => void;
}

/**
 * Modal component to notify users when their session has expired
 *
 * Features:
 * - Cannot be dismissed by clicking outside or pressing Escape
 * - Clear explanation of why session expired
 * - Countdown timer that automatically redirects to sign-in
 * - Single "Sign In Again" action button for immediate manual redirect
 * - Preserves current path for post-login redirect (handled by parent)
 *
 * @example
 * ```tsx
 * <TokenExpirationModal
 *   isOpen={tokenExpired}
 *   onSignInClick={handleSignInAgain}
 * />
 * ```
 */
export const TokenExpirationModal: React.FC<TokenExpirationModalProps> = ({
  isOpen,
  onSignInClick,
}) => {
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const redirectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Clean up any running timers when modal closes
      if (timerRef.current) clearInterval(timerRef.current);
      if (redirectRef.current) clearTimeout(redirectRef.current);
      setCountdown(AUTO_REDIRECT_SECONDS);
      return;
    }

    // Reset countdown when modal opens
    setCountdown(AUTO_REDIRECT_SECONDS);

    // Tick countdown every second for display
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
        }
        return Math.max(0, prev - 1);
      });
    }, 1000);

    // Auto-redirect after the countdown expires
    redirectRef.current = setTimeout(() => {
      onSignInClick();
    }, AUTO_REDIRECT_SECONDS * 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, [isOpen, onSignInClick]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        // Prevent closing via close button or overlay click
        // User must click "Sign In Again" button
      }}
      title="Session Expired"
      size="sm"
      closeOnOverlayClick={false} // Prevent dismissal via overlay
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onSignInClick}
            className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors duration-200"
          >
            Sign In Again{countdown > 0 ? ` (${countdown})` : ''}
          </button>
        </div>
      }
    >
      <div className="py-4 space-y-4">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="rounded-full bg-yellow-100 dark:bg-yellow-900/30 p-3">
            <svg
              className="h-8 w-8 text-yellow-600 dark:text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Message */}
        <div className="text-center space-y-3">
          <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
            Your session has expired for security reasons.
          </p>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            Please sign in again to continue using the application. You'll be redirected back to
            this page after signing in.
          </p>
        </div>

        {/* Additional Info */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start space-x-3">
            <svg
              className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Sessions expire automatically after a period of inactivity to protect your account.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
